import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import type {FC, ReactNode} from 'react';
import {useAppSelector} from 'src/app/reduxCustomHooks';
import {TaskRo} from 'src/app/services/api.generated';
import {useFirstDocGuidance} from 'src/components/hooks/useFirstDocGuidance';

export const quickStartStorageKey = (username?: string | null) =>
  `anonify-quick-start:${username || 'guest'}`;

type FirstDocumentGuideContextValue = {
  /** Non-demo + resolved tasks: panel/reopen available (incl. busy accounts). */
  visible: boolean;
  open: boolean;
  toggle: (value: boolean) => void;
  reviewTask?: TaskRo;
};

const FirstDocumentGuideContext = createContext<FirstDocumentGuideContextValue | null>(null);

export const FirstDocumentGuideProvider: FC<{children: ReactNode}> = ({children}) => {
  const username = useAppSelector((state) => state.auth.user?.username);
  const storageKey = quickStartStorageKey(username);
  const {canShow, shouldAutoOpen, tasks} = useFirstDocGuidance();
  const [expanded, setExpanded] = useState<boolean | null>(null);

  useEffect(() => {
    try {
      const value = localStorage.getItem(storageKey);
      if (value === 'collapsed') {
        setExpanded(false);
        return;
      }
      // Ignore stored "expanded" on busy accounts so the panel never auto-opens;
      // reopen click still sets expanded via toggle().
      if (value === 'expanded' && shouldAutoOpen) {
        setExpanded(true);
        return;
      }
      setExpanded(null);
    } catch {
      setExpanded(null);
    }
  }, [storageKey, shouldAutoOpen]);

  const toggle = useCallback((value: boolean) => {
    setExpanded(value);
    try {
      localStorage.setItem(storageKey, value ? 'expanded' : 'collapsed');
    } catch {
      /* ignore quota / private mode */
    }
  }, [storageKey]);

  const value = useMemo<FirstDocumentGuideContextValue>(() => ({
    visible: canShow,
    // Empty: default open. Busy: default closed. Explicit toggle always wins.
    open: canShow && (expanded ?? shouldAutoOpen),
    toggle,
    reviewTask: tasks.find((task) => task.state === 'READY'),
  }), [canShow, shouldAutoOpen, expanded, tasks, toggle]);

  return (
    <FirstDocumentGuideContext.Provider value={value}>
      {children}
    </FirstDocumentGuideContext.Provider>
  );
};

export const useFirstDocumentGuide = () => {
  const ctx = useContext(FirstDocumentGuideContext);
  if (!ctx) {
    throw new Error('useFirstDocumentGuide must be used within FirstDocumentGuideProvider');
  }
  return ctx;
};
